import {
  Animated,
  BackHandler,
  Modal,
  NativeEventSubscription,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import React, {useEffect, useRef} from 'react';

import CCColors from '../../Utils/CCColors';
import LottieView from 'react-native-web-lottie';
import {FontSizes, WINDOW_HEIGHT, WINDOW_WIDTH} from '../../Utils/Dimensions';
import CCText from '../CCText';

interface LoaderProps {
  loading: boolean;
  fullScreen?: boolean;
  loadingIcon?: () => JSX.Element;
  bgStyle?: ViewStyle;
  bgWhite?: boolean;
  isComponentLoader?: boolean;
  textToShow?: string;
  textColor?: string;
}

const Loader: React.FC<LoaderProps> = (props: LoaderProps) => {
  const {
    loading,
    fullScreen = false,
    bgStyle,
    bgWhite = false,
    isComponentLoader = false,
    textToShow,
    textColor = `${CCColors.White}`,
  } = props;

  const spinAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const backAction = () => {
      return true; // This blocks the back button
    };

    let backHandler: NativeEventSubscription | null = null;
    if (loading) {
      backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        backAction,
      );
    } else {
      if (backHandler) {
        // @ts-ignore
        backHandler?.remove();
      }
    }
    return () => backHandler?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spin = spinAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  if (isComponentLoader) {
    if (!loading) {
      return;
    }
    return (
      // <View
      //   style={[
      //     styles.modalBackground,
      //     bgStyle,
      //     bgWhite && {backgroundColor: CCColors?.White},
      //   ]}>
      //   <View style={styles.activityIndicatorWrapper}>
      <View
        style={{
          transform: [{perspective: 1000}],
          margin: 'auto',
          opacity: 0.8,
        }}>
        <LottieView
          source={require('../../Assets/Animation/chessboardLoader.json')}
          style={{width: WINDOW_WIDTH / 4, height: WINDOW_HEIGHT / 4}}
          autoPlay
          loop
        />
      </View>
      // </View>
      // </View>;
    );
  }
  if (!loading) {
    return null;
  }

  const loaderContent = (
    <View style={styles.activityIndicatorWrapper}>
      <View style={{transform: [{perspective: 1000}]}}>
        <LottieView
          source={require('../../Assets/Animation/chessboardLoader.json')}
          style={{width: WINDOW_WIDTH / 4, height: WINDOW_HEIGHT / 4}}
          autoPlay
          loop
        />
        {textToShow && (
          <CCText color={textColor} fontSize={FontSizes[16]} textAlign="center">
            {textToShow}
          </CCText>
        )}
      </View>
    </View>
  );

  // bgWhite is the in-place "this screen/panel is loading" placeholder, so it
  // stays inline and scoped to its own container -- a full-viewport portal
  // would blank the side nav / other panels, flash an empty frame on mount
  // (RNW's ModalAnimation renders opacity: 0 on first commit), and escape
  // `display: 'none'` tab panels. Only the dimming overlay below needs the
  // Modal portal.
  if (bgWhite) {
    return (
      <View
        style={[
          styles.inlineBackground,
          fullScreen ? styles.fullScreenLoader : styles.modalOverlay,
          bgStyle,
          {backgroundColor: CCColors?.White},
        ]}>
        {loaderContent}
      </View>
    );
  }

  // A Modal (not a plain View), even though the inner content is already
  // `position: 'fixed'` -- react-native-web's Modal renders through a portal
  // straight to document.body, which is the only thing that reliably escapes
  // an ancestor with its own CSS transform (e.g. the Drawer navigator's
  // slide-transition wrapper). A transformed ancestor becomes the
  // "containing block" for `fixed` per the CSS spec, silently turning it
  // back into `absolute` relative to that ancestor's box -- which is exactly
  // why the overlay was still getting cut off partway down the page instead
  // of covering the full viewport.
  return (
    <Modal transparent animationType="none" visible={loading}>
      <View
        style={[
          styles.modalBackground,
          fullScreen ? styles.fullScreenLoader : styles.modalOverlay,
          bgStyle,
          bgWhite && {backgroundColor: CCColors?.White},
        ]}>
        {loaderContent}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalBackground: {
    // `fixed` (not `absolute`) -- this is nested arbitrarily deep inside
    // whichever screen renders it, often inside a scrollable content
    // container. `absolute` sizes against that ancestor's full scrollable
    // height/nearest positioned ancestor, not the actual viewport, so the
    // dark dimming background only covered part of the screen (or ran past
    // it) depending on scroll position. `fixed` is always viewport-relative,
    // matching modalOverlay/fullScreenLoader's 100vh below.
    position: 'fixed' as any,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#00000063',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    elevation: 9999,
  },
  inlineBackground: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#00000063',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    elevation: 9999,
  },
  modalOverlay: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100vh',
  },
  fullScreenLoader: {
    width: '100%',
    height: '100vh',
    backgroundColor: '#00000063',
  },
  activityIndicatorWrapper: {
    backgroundColor: 'transparent',
    // height: 100,
    // width: 100,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    zIndex: 9999999999999,
  },
});

export default Loader;
